import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Keeps a scrollable container pinned to the bottom as content grows, but
 * respects the user if they have scrolled up to read older content.
 *
 * Pin/unpin is driven by user scroll events (distance from bottom). The
 * caller decides *when* to consider scrolling (e.g. on new messages or
 * streamed tokens) and uses `scrollToBottomIfPinned` — that way incidental
 * layout changes like expanding a tool-call detail panel don't yank the
 * view to the bottom.
 */

const DEFAULT_THRESHOLD_PX = 16;

export interface UseStickToBottomOptions {
  /**
   * Distance from the bottom (in px) still considered "at bottom". A small
   * slop handles sub-pixel rounding and the case where the user scrolls up
   * only a little.
   */
  threshold?: number;
}

export interface UseStickToBottomResult {
  /** Attach to the scroll container. */
  scrollRef: (node: HTMLDivElement | null) => void;
  /** True when the view is locked to the bottom. */
  isPinned: boolean;
  /**
   * Scroll to the bottom unconditionally. Callers use this for "force" cases
   * — the user just sent a message, or clicked the "New messages" button.
   */
  scrollToBottom: (behavior?: ScrollBehavior) => void;
  /**
   * Scroll to the bottom only if the view is currently pinned. Call this
   * when streaming content updates so the view follows the latest message
   * without overriding a user who has scrolled up.
   */
  scrollToBottomIfPinned: (behavior?: ScrollBehavior) => void;
}

export function useStickToBottom({
  threshold = DEFAULT_THRESHOLD_PX,
}: UseStickToBottomOptions = {}): UseStickToBottomResult {
  const scrollElRef = useRef<HTMLDivElement | null>(null);
  const scrollListenerRef = useRef<(() => void) | null>(null);
  const cleanupUserIntentRef = useRef<(() => void) | null>(null);
  const pinnedRef = useRef(true);
  // Timestamp (ms) until which programmatic scrolls are in flight; the scroll
  // listener ignores events inside this window so an in-progress `behavior:
  // 'smooth'` animation can't flip `pinned` to false on its way to the bottom.
  // Without this, sending a user message unpins the view mid-animation and
  // subsequent streaming tokens no longer auto-follow.
  const programmaticUntilRef = useRef(0);
  // Timestamp (ms) until which the user "owns" the scroll position. While the
  // window is open, `scrollToBottomIfPinned` is a no-op no matter what
  // `pinnedRef` says — so a streaming tick can't yank the user away even if
  // their tiny trackpad scroll left them within `threshold` of the bottom and
  // the scroll listener re-pinned them. Each user-intent event extends the
  // window, so a continuous gesture keeps suppressing auto-follow.
  const userOwnedUntilRef = useRef(0);
  const [isPinned, setIsPinned] = useState(true);

  const updatePinned = useCallback((value: boolean) => {
    if (pinnedRef.current === value) return;
    pinnedRef.current = value;
    setIsPinned(value);
  }, []);

  const scrollToBottomInternal = useCallback((behavior: ScrollBehavior) => {
    const el = scrollElRef.current;
    if (!el) return;
    // Browser default smooth-scroll animation is ~300ms; a little headroom
    // covers slow frames. Instant scrolls still set the flag so the scroll
    // event dispatched by `scrollTo` itself can't unpin us.
    programmaticUntilRef.current = Date.now() + (behavior === 'smooth' ? 600 : 80);
    el.scrollTo({ top: el.scrollHeight, behavior });
  }, []);

  const scrollToBottom = useCallback(
    (behavior: ScrollBehavior = 'smooth') => {
      // Explicit force scroll: user just sent a message or clicked the
      // "new messages" pill. Tear down any prior ownership window so
      // streaming auto-follow resumes immediately.
      userOwnedUntilRef.current = 0;
      scrollToBottomInternal(behavior);
      updatePinned(true);
    },
    [scrollToBottomInternal, updatePinned]
  );

  const scrollToBottomIfPinned = useCallback(
    (behavior: ScrollBehavior = 'auto') => {
      if (Date.now() < userOwnedUntilRef.current) return;
      if (!pinnedRef.current) return;
      scrollToBottomInternal(behavior);
    },
    [scrollToBottomInternal]
  );

  const scrollRef = useCallback(
    (node: HTMLDivElement | null) => {
      const prev = scrollElRef.current;
      if (prev && scrollListenerRef.current) {
        prev.removeEventListener('scroll', scrollListenerRef.current);
        scrollListenerRef.current = null;
      }
      cleanupUserIntentRef.current?.();
      cleanupUserIntentRef.current = null;
      scrollElRef.current = node;
      if (!node) return;
      const onScroll = () => {
        // Ignore scroll events caused by our own programmatic scrolls — the
        // smooth animation passes through many intermediate scrollTop values
        // that would otherwise read as "not at bottom" and unpin the view.
        if (Date.now() < programmaticUntilRef.current) return;
        const atBottom = node.scrollHeight - node.scrollTop - node.clientHeight < threshold;
        updatePinned(atBottom);
      };
      scrollListenerRef.current = onScroll;
      node.addEventListener('scroll', onScroll, { passive: true });

      // User intent: any wheel / touchmove / scrollbar drag / nav key grants
      // the user a 1.5s "ownership window" during which streaming auto-follow
      // is suppressed regardless of pin state. Continuing to interact extends
      // the window. We also flip `pinnedRef` synchronously so the visible
      // pin badge updates immediately — but the window is what guarantees no
      // auto-scroll, even if the scroll listener later re-pins because the
      // user's tiny trackpad scroll left them within `threshold` of the
      // bottom.
      const USER_OWNERSHIP_MS = 1500;
      const grantOwnership = () => {
        userOwnedUntilRef.current = Date.now() + USER_OWNERSHIP_MS;
        programmaticUntilRef.current = 0;
        updatePinned(false);
      };
      const onWheel = (event: WheelEvent) => {
        // Wheel-down at the bottom doesn't need ownership — the user is just
        // signalling "follow more content". Ignore so streaming auto-follow
        // continues. Any non-positive delta (up or zero) grants ownership.
        if (event.deltaY <= 0) grantOwnership();
      };
      const onTouchMove = () => grantOwnership();
      const onMouseDown = (event: MouseEvent) => {
        // Scrollbar lives in the gutter beyond `clientWidth` (which excludes
        // the scrollbar). A mousedown there is a scrollbar drag — the most
        // common way users escape an auto-following chat.
        if (event.offsetX > node.clientWidth) grantOwnership();
      };
      const UP_NAV_KEYS = new Set(['ArrowUp', 'PageUp', 'Home']);
      const onKeyDown = (event: KeyboardEvent) => {
        if (UP_NAV_KEYS.has(event.key)) grantOwnership();
      };
      node.addEventListener('wheel', onWheel, { passive: true });
      node.addEventListener('touchmove', onTouchMove, { passive: true });
      node.addEventListener('mousedown', onMouseDown);
      node.addEventListener('keydown', onKeyDown);
      cleanupUserIntentRef.current = () => {
        node.removeEventListener('wheel', onWheel);
        node.removeEventListener('touchmove', onTouchMove);
        node.removeEventListener('mousedown', onMouseDown);
        node.removeEventListener('keydown', onKeyDown);
      };
    },
    [threshold, updatePinned]
  );

  useEffect(() => {
    return () => {
      const el = scrollElRef.current;
      if (el && scrollListenerRef.current) {
        el.removeEventListener('scroll', scrollListenerRef.current);
        scrollListenerRef.current = null;
      }
      cleanupUserIntentRef.current?.();
      cleanupUserIntentRef.current = null;
    };
  }, []);

  return { scrollRef, isPinned, scrollToBottom, scrollToBottomIfPinned };
}
